import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import useApiCall from "./useApiCall";

const useDirectorDecisions = (ep, inscriptions, { fetchEp } = {}) => {
  const apiCall = useApiCall();
  const [directorDecisions, setDirectorDecisions] = useState({});
  const [surseoirInscription, setSurseoirInscription] = useState(null);
  const [isSavingDirector, setIsSavingDirector] = useState(null);
  const [isBulkDirectorSaving, setIsBulkDirectorSaving] = useState(false);
  const [showBulkDirectorModal, setShowBulkDirectorModal] = useState(false);
  const [overwriteDirectorExisting, setOverwriteDirectorExisting] =
    useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);

  const isDirectorView = !!ep?.closedAt;

  useEffect(() => {
    if (inscriptions.length > 0 && isDirectorView) {
      const initial = {};
      inscriptions.forEach((i) => {
        if (i.directorDecision) {
          initial[i.id] = {
            decision: i.directorDecision,
            comment: i.directorNote || null,
          };
        }
      });
      setDirectorDecisions(initial);
    }
  }, [inscriptions, isDirectorView]);

  const pendingDirectorCount = inscriptions.filter(
    (i) => !directorDecisions[i.id],
  ).length;
  const canCloseDefinitively =
    pendingDirectorCount === 0 && inscriptions.length > 0;

  const handleDirectorAccord = useCallback(
    (inscription) => {
      setIsSavingDirector(inscription.id);
      apiCall(
        "patch",
        `inscriptions-ep/${inscription.id}/valider-sanction-finale`,
        { directorDecision: "ACCORD", directorNote: null },
        () => {
          setDirectorDecisions((prev) => ({
            ...prev,
            [inscription.id]: { decision: "ACCORD", comment: null },
          }));
          setIsSavingDirector(null);
        },
        () => {
          toast.error(
            "Erreur lors de l'enregistrement de la décision directeur",
          );
          setIsSavingDirector(null);
        },
      );
    },
    [apiCall],
  );

  const handleDirectorSurseoir = useCallback((inscription) => {
    setSurseoirInscription(inscription);
  }, []);

  const handleConfirmSurseoir = useCallback(
    (comment) => {
      setIsSavingDirector(surseoirInscription.id);
      apiCall(
        "patch",
        `inscriptions-ep/${surseoirInscription.id}/valider-sanction-finale`,
        { directorDecision: "A_SURSEOIR", directorNote: comment },
        () => {
          setDirectorDecisions((prev) => ({
            ...prev,
            [surseoirInscription.id]: { decision: "A_SURSEOIR", comment },
          }));
          setIsSavingDirector(null);
          setSurseoirInscription(null);
        },
        () => {
          toast.error(
            "Erreur lors de l'enregistrement de la décision directeur",
          );
          setIsSavingDirector(null);
        },
      );
    },
    [apiCall, surseoirInscription],
  );

  const handleEndSession = useCallback(() => {
    if (!canCloseDefinitively) return;
    if (
      window.confirm(
        "Attention : Cette action est irréversible. L'EP sera finalisé.\n\nConfirmer ?",
      )
    ) {
      setIsEndingSession(true);
      apiCall(
        "patch",
        `eps/${ep.id}/finaliser`,
        {},
        () => {
          toast.success("EP finalisée avec succès");
          setIsEndingSession(false);
          if (fetchEp) fetchEp();
        },
        () => {
          setIsEndingSession(false);
        },
      );
    }
  }, [apiCall, canCloseDefinitively, ep?.id, fetchEp]);

  const handleDirectorBulkAccord = useCallback((selectedIds) => {
    if (selectedIds.length === 0) return;
    setOverwriteDirectorExisting(false);
    setShowBulkDirectorModal(true);
  }, []);

  const handleConfirmBulkDirectorAccord = useCallback(
    (selectedIds, clearSelection) => {
      const selectedInscriptions = inscriptions.filter((i) =>
        selectedIds.includes(i.id),
      );

      const hasNonAccord = selectedInscriptions.some(
        (i) =>
          directorDecisions[i.id] &&
          directorDecisions[i.id].decision !== "ACCORD",
      );

      let idsToSend;
      if (overwriteDirectorExisting || !hasNonAccord) {
        idsToSend = selectedIds;
      } else {
        idsToSend = selectedInscriptions
          .filter(
            (i) =>
              !directorDecisions[i.id] ||
              directorDecisions[i.id].decision === "ACCORD",
          )
          .map((i) => i.id);
      }

      if (idsToSend.length === 0) {
        toast.warning(
          "Toutes les inscriptions sélectionnées ont déjà une décision directeur différente de ACCORD. Cochez l'option pour les écraser.",
        );
        return;
      }

      setIsBulkDirectorSaving(true);
      apiCall(
        "patch",
        `eps/${ep.id}/valider-decisions-finales`,
        { ids: idsToSend },
        () => {
          const updates = {};
          idsToSend.forEach((id) => {
            updates[id] = { decision: "ACCORD", comment: null };
          });
          setDirectorDecisions((prev) => ({ ...prev, ...updates }));
          clearSelection();
          setIsBulkDirectorSaving(false);
          setShowBulkDirectorModal(false);
          toast.success(`${idsToSend.length} accord(s) enregistré(s)`);
        },
        () => {
          toast.error("Erreur lors de la validation en masse");
          setIsBulkDirectorSaving(false);
        },
      );
    },
    [
      apiCall,
      directorDecisions,
      ep?.id,
      inscriptions,
      overwriteDirectorExisting,
    ],
  );

  return {
    directorDecisions,
    surseoirInscription,
    setSurseoirInscription,
    isSavingDirector,
    isBulkDirectorSaving,
    showBulkDirectorModal,
    setShowBulkDirectorModal,
    overwriteDirectorExisting,
    setOverwriteDirectorExisting,
    isEndingSession,
    pendingDirectorCount,
    canCloseDefinitively,
    handleDirectorAccord,
    handleDirectorSurseoir,
    handleConfirmSurseoir,
    handleEndSession,
    handleDirectorBulkAccord,
    handleConfirmBulkDirectorAccord,
  };
};

export default useDirectorDecisions;
